import React from 'react';

/**
 * Campos "puxados para a Visibilidade" do RSVP: Quantidade de HCP, Speakers Confirmados e
 * todos os pacotes (Aéreo, Hospedagem, Transfer, Inscrição e Completo).
 *
 * Reutilizado em dois contextos do formulário de Start do RSVP:
 *  - com o RSVP ativo (edição padrão);
 *  - apenas com a Visibilidade ativa (RSVP ainda inativo), para capturar os números já na visibilidade.
 *
 * A função `campoPodeEditar` é injetada pelo pai para definir a regra de edição de cada contexto
 * (na Visibilidade, os campos ficam livres até o Start ser enviado).
 * `switchTravadoLigado` (também injetada) rege só os SWITCHES dos pacotes: após o Start eles podem
 * ser ligados, mas um pacote já salvo como ligado não pode ser desligado.
 */
export default function CamposVisibilidadeRsvp({ dados, setDados, campoPodeEditar, switchTravadoLigado = () => false, labelObservacao, obrigatorio }) {
    return (
        <>
            <div className="row">
                <div className='col-md-6 d-none d-md-flex'></div>
                <div className="col-md-6">
                    <div className="form-group">
                        {labelObservacao('quantidade_pessoas', 'Total de Participantes Esperados')}
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Número estimado de participantes"
                            value={dados.quantidadePessoas || ''}
                            disabled
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        {labelObservacao('quantidade_hcp', <>Quantidade de HCP {obrigatorio}</>)}
                        <input
                            required
                            min="0"
                            type="number"
                            className="form-control"
                            placeholder="Healthcare Professionals esperados"
                            value={dados.evento_rsvp_data?.quantidade_hcp ?? ''}
                            disabled={!campoPodeEditar('quantidade_hcp')}
                            onChange={(e) => {
                                const rsvp = dados.evento_rsvp_data || {};
                                rsvp.quantidade_hcp = e.target.value;
                                setDados({ ...dados, evento_rsvp_data: rsvp });
                            }}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        {labelObservacao('speakers_confirmados', <>Speakers Confirmados {obrigatorio}</>)}
                        <input
                            required
                            min="0"
                            type="number"
                            className="form-control"
                            placeholder="Quantidade de palestrantes"
                            value={dados.evento_rsvp_data?.speakers_confirmados ?? ''}
                            disabled={!campoPodeEditar('speakers_confirmados')}
                            onChange={(e) => {
                                const rsvp = dados.evento_rsvp_data || {};
                                rsvp.speakers_confirmados = e.target.value;
                                setDados({ ...dados, evento_rsvp_data: rsvp });
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    <div className="form-group">
                        <div className="form-check form-switch d-flex align-items-center">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                checked={dados.evento_rsvp_data?.pacote_aereo_quantidade !== null && dados.evento_rsvp_data?.pacote_aereo_quantidade !== undefined}
                                disabled={switchTravadoLigado('pacote_aereo_quantidade')}
                                title={switchTravadoLigado('pacote_aereo_quantidade') ? 'Não pode ser desativado após o Start.' : undefined}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    if (!e.target.checked) {
                                        rsvp.pacote_aereo_quantidade = null;
                                    } else {
                                        rsvp.pacote_aereo_quantidade = 0;
                                    }
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                            {labelObservacao('pacote_aereo_quantidade', 'Pacote Aéreo', { className: 'form-check-label ms-2' })}
                        </div>
                        {dados.evento_rsvp_data?.pacote_aereo_quantidade !== null && dados.evento_rsvp_data?.pacote_aereo_quantidade !== undefined && (
                            <input
                                required
                                min="0"
                                type="number"
                                className="form-control mt-2"
                                placeholder="Quantidade"
                                value={dados.evento_rsvp_data?.pacote_aereo_quantidade || ''}
                                disabled={!campoPodeEditar('pacote_aereo_quantidade')}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    rsvp.pacote_aereo_quantidade = parseInt(e.target.value) || 0;
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <div className="form-check form-switch d-flex align-items-center">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                checked={dados.evento_rsvp_data?.pacote_hospedagem_quantidade !== null && dados.evento_rsvp_data?.pacote_hospedagem_quantidade !== undefined}
                                disabled={switchTravadoLigado('pacote_hospedagem_quantidade')}
                                title={switchTravadoLigado('pacote_hospedagem_quantidade') ? 'Não pode ser desativado após o Start.' : undefined}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    if (!e.target.checked) {
                                        rsvp.pacote_hospedagem_quantidade = null;
                                        rsvp.numero_hospedagens = null; // espelho persistido — acompanha o pacote
                                    } else {
                                        rsvp.pacote_hospedagem_quantidade = 0;
                                    }
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                            {labelObservacao('pacote_hospedagem_quantidade', 'Pacote Hospedagem', { className: 'form-check-label ms-2' })}
                        </div>
                        {dados.evento_rsvp_data?.pacote_hospedagem_quantidade !== null && dados.evento_rsvp_data?.pacote_hospedagem_quantidade !== undefined && (
                            <input
                                required
                                min="0"
                                type="number"
                                className="form-control mt-2"
                                placeholder="Quantidade"
                                value={dados.evento_rsvp_data?.pacote_hospedagem_quantidade || ''}
                                disabled={!campoPodeEditar('pacote_hospedagem_quantidade')}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    const valor = parseInt(e.target.value) || 0;
                                    rsvp.pacote_hospedagem_quantidade = valor;
                                    rsvp.numero_hospedagens = valor; // Preenche também o Número de Hospedagens
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <div className="form-check form-switch d-flex align-items-center">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                checked={dados.evento_rsvp_data?.pacote_transfer_quantidade !== null && dados.evento_rsvp_data?.pacote_transfer_quantidade !== undefined}
                                disabled={switchTravadoLigado('pacote_transfer_quantidade')}
                                title={switchTravadoLigado('pacote_transfer_quantidade') ? 'Não pode ser desativado após o Start.' : undefined}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    if (!e.target.checked) {
                                        rsvp.pacote_transfer_quantidade = null;
                                    } else {
                                        rsvp.pacote_transfer_quantidade = 0;
                                    }
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                            {labelObservacao('pacote_transfer_quantidade', 'Pacote Transfer', { className: 'form-check-label ms-2' })}
                        </div>
                        {dados.evento_rsvp_data?.pacote_transfer_quantidade !== null && dados.evento_rsvp_data?.pacote_transfer_quantidade !== undefined && (
                            <input
                                required
                                min="0"
                                type="number"
                                className="form-control mt-2"
                                placeholder="Quantidade"
                                value={dados.evento_rsvp_data?.pacote_transfer_quantidade || ''}
                                disabled={!campoPodeEditar('pacote_transfer_quantidade')}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    rsvp.pacote_transfer_quantidade = parseInt(e.target.value) || 0;
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <div className="form-check form-switch d-flex align-items-center">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                checked={dados.evento_rsvp_data?.pacote_inscricao_quantidade !== null && dados.evento_rsvp_data?.pacote_inscricao_quantidade !== undefined}
                                disabled={switchTravadoLigado('numero_inscricoes')}
                                title={switchTravadoLigado('numero_inscricoes') ? 'Não pode ser desativado após o Start.' : undefined}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    if (!e.target.checked) {
                                        rsvp.pacote_inscricao_quantidade = null;
                                        rsvp.numero_inscricoes = null; // coluna real — sem isso o valor antigo re-liga o switch no reload
                                    } else {
                                        rsvp.pacote_inscricao_quantidade = 0;
                                    }
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                            {labelObservacao('numero_inscricoes', 'Pacote Inscrição', { className: 'form-check-label ms-2' })}
                        </div>
                        {dados.evento_rsvp_data?.pacote_inscricao_quantidade !== null && dados.evento_rsvp_data?.pacote_inscricao_quantidade !== undefined && (
                            <input
                                required
                                type="number"
                                min="0"
                                className="form-control mt-2"
                                placeholder="Quantidade"
                                value={dados.evento_rsvp_data?.pacote_inscricao_quantidade || ''}
                                disabled={!campoPodeEditar('numero_inscricoes')}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    const valor = parseInt(e.target.value) || 0;
                                    rsvp.pacote_inscricao_quantidade = valor;
                                    rsvp.numero_inscricoes = valor; // Salva também em numero_inscricoes
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <div className="form-check form-switch d-flex align-items-center">
                            <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                checked={dados.evento_rsvp_data?.pacote_completo_quantidade !== null && dados.evento_rsvp_data?.pacote_completo_quantidade !== undefined}
                                disabled={switchTravadoLigado('pacote_completo_quantidade')}
                                title={switchTravadoLigado('pacote_completo_quantidade') ? 'Não pode ser desativado após o Start.' : undefined}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    if (!e.target.checked) {
                                        rsvp.pacote_completo_quantidade = null;
                                    } else {
                                        rsvp.pacote_completo_quantidade = 0;
                                    }
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                            {labelObservacao('pacote_completo_quantidade', 'Pacote Completo', { className: 'form-check-label ms-2' })}
                        </div>
                        {dados.evento_rsvp_data?.pacote_completo_quantidade !== null && dados.evento_rsvp_data?.pacote_completo_quantidade !== undefined && (
                            <input
                                required
                                min="0"
                                type="number"
                                className="form-control mt-2"
                                placeholder="Quantidade"
                                value={dados.evento_rsvp_data?.pacote_completo_quantidade || ''}
                                disabled={!campoPodeEditar('pacote_completo_quantidade')}
                                onChange={(e) => {
                                    const rsvp = dados.evento_rsvp_data || {};
                                    rsvp.pacote_completo_quantidade = parseInt(e.target.value) || 0;
                                    setDados({ ...dados, evento_rsvp_data: rsvp });
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
